/**
 * Reusable unsaved-changes framework.
 *
 * Architecture:
 *   - <UnsavedChangesRegistryProvider> lives near the app root and holds a set of
 *     "active dirty guards". Each guard exposes an async `check()` fn that pops
 *     the confirmation dialog and returns true if navigation should proceed.
 *   - `useUnsavedChanges` registers itself in the registry whenever it becomes dirty.
 *   - `UnsavedChangesGuard` (mounted once inside a panel) sets up:
 *       (1) document-level click interception on <a> elements (capture phase — runs
 *           BEFORE react-router's own Link handler, giving us a synchronous e.preventDefault),
 *       (2) popstate listener with a sentinel history entry (so browser Back triggers
 *           the confirmation before actually leaving),
 *       (3) beforeunload listener for hard refresh / tab close.
 *   - `useGuardedTabsChange(setter)` returns an onValueChange handler that consults
 *     the registry before flipping tabs.
 *
 * Usage:
 *   const { dirty, ... } = useUnsavedChanges({ initial, onSave });
 *   <UnsavedChangesGuard onSave={save} />           // in the same panel
 *   <SaveBar dirty={dirty} saving={saving} ... />
 *
 *   const onValueChange = useGuardedTabsChange(setTab);
 *   <Tabs value={tab} onValueChange={onValueChange}>...</Tabs>
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import { useConfirm } from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { Loader2, RotateCcw, Save } from "lucide-react";

const eq = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// --- Global registry --------------------------------------------------------
const RegistryCtx = createContext(null);

export function UnsavedChangesRegistryProvider({ children }) {
  const guardsRef = useRef(new Map());
  const runningRef = useRef(null); // Promise dedup so concurrent runAll() share one prompt

  const register = useCallback((id, check) => {
    guardsRef.current.set(id, check);
    return () => guardsRef.current.delete(id);
  }, []);

  const runAll = useCallback(async () => {
    if (runningRef.current) return runningRef.current;
    const p = (async () => {
      for (const check of guardsRef.current.values()) {
        const ok = await check();
        if (!ok) return false;
      }
      return true;
    })();
    runningRef.current = p;
    try { return await p; } finally { runningRef.current = null; }
  }, []);

  const anyDirty = useCallback(() => guardsRef.current.size > 0, []);

  const value = useMemo(() => ({ register, runAll, anyDirty }), [register, runAll, anyDirty]);
  return <RegistryCtx.Provider value={value}>{children}</RegistryCtx.Provider>;
}

export function useUnsavedRegistry() {
  return useContext(RegistryCtx);
}

// --- State container --------------------------------------------------------
export function useUnsavedChanges({ initial, onSave, onDiscard, autoSaveIndicatorMs = 2500 }) {
  const [baseline, setBaseline] = useState(initial);
  const [values, setValues] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [errors, setErrors] = useState(null);
  const savedTimer = useRef(null);

  useEffect(() => {
    setBaseline((prev) => (eq(prev, initial) ? prev : initial));
    setValues((prev) => (eq(prev, baseline) ? initial : prev));
  }, [JSON.stringify(initial)]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = useMemo(() => !eq(values, baseline), [values, baseline]);

  const setValue = useCallback((key, val) => {
    setValues((v) => (typeof key === "function" ? key(v) : { ...v, [key]: val }));
  }, []);

  const save = useCallback(async () => {
    if (saving || !dirty) return true;
    setSaving(true);
    setErrors(null);
    try {
      const result = await onSave(values);
      const next = (result && typeof result === "object" && !Array.isArray(result)) ? { ...values, ...result } : values;
      setBaseline(next);
      setValues(next);
      setSavedAt(new Date());
      if (savedTimer.current) clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSavedAt(null), autoSaveIndicatorMs);
      return true;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || e?.message || "Save failed");
      setErrors(msg);
      toast.error(msg);
      return false;
    } finally { setSaving(false); }
  }, [saving, dirty, onSave, values, autoSaveIndicatorMs]);

  const discard = useCallback(() => {
    setValues(baseline);
    setErrors(null);
    onDiscard?.();
  }, [baseline, onDiscard]);

  return { dirty, setValue, values, setValues, save, discard, savedAt, saving, errors };
}

// --- Sticky Save bar --------------------------------------------------------
export function SaveBar({ dirty, saving, savedAt, errors, onSave, onDiscard, testId = "save-bar", label = "You have unsaved changes." }) {
  if (!dirty) {
    if (savedAt) {
      return (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-4 z-50 border border-[#30D158]/40 bg-card px-3 py-1.5 mono text-[11px] pnl-up uppercase tracking-widest" data-testid={`${testId}-saved`}>
          Saved · {savedAt.toLocaleTimeString()}
        </div>
      );
    }
    return null;
  }
  return (
    <div
      className="fixed left-4 right-4 md:left-1/2 md:right-auto md:-translate-x-1/2 bottom-4 z-50 border border-signal-yellow/40 bg-card shadow-xl"
      data-testid={testId}
    >
      <div className="flex items-center gap-3 px-4 py-2.5">
        <span className="w-1.5 h-1.5 bg-signal-yellow animate-pulse" />
        <span className="mono uppercase tracking-widest text-[11px] text-signal-yellow">{label}</span>
        {errors && <span className="mono text-[10px] pnl-down truncate max-w-xs">· {errors}</span>}
        <div className="ml-auto flex items-center gap-2">
          <button className="btn-outline h-7 !px-2 text-[11px]" onClick={onDiscard} disabled={saving} data-testid={`${testId}-discard`}>
            <RotateCcw size={11} /> Discard
          </button>
          <button className="btn-primary h-7 !px-3 text-[11px]" onClick={onSave} disabled={saving} data-testid={`${testId}-save`}>
            {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Navigation guard -------------------------------------------------------
/**
 * Mount once per dirty-aware panel. Registers itself in the registry so the
 * capture-phase Link click interceptor + tab switch guard can consult it.
 *
 * NOTE: For the browser Back button we push a sentinel history entry when the
 * panel becomes dirty. The popstate listener re-pushes the sentinel while
 * prompting so the user stays in place. When the user confirms leave, we
 * history.back() twice (once to pop the sentinel, once for the real back).
 */
export function UnsavedChangesGuard({ dirty, onSave, discardOnLeave = true, panelId }) {
  const confirm = useConfirm();
  const registry = useUnsavedRegistry();
  const idRef = useRef(panelId || `guard-${Math.random().toString(36).slice(2, 8)}`);
  const dirtyRef = useRef(dirty);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);

  const runPrompt = useCallback(async () => {
    return await confirm.confirm({
      variant: "unsaved",
      title: "Unsaved changes",
      description: "You have edited settings that haven't been saved. What would you like to do?",
      confirmLabel: "Save and Continue",
      cancelLabel: "Discard Changes",
      singletonKey: "unsaved-changes",
    });
  }, [confirm]);

  const check = useCallback(async () => {
    if (!dirtyRef.current) return true;
    const result = await runPrompt();
    if (result === true) {
      const ok = await onSave?.();
      return ok !== false;
    }
    if (result === false && discardOnLeave) return true;
    return false;
  }, [runPrompt, onSave, discardOnLeave]);

  // Register/unregister with the global registry based on dirty
  useEffect(() => {
    if (!registry || !dirty) return;
    return registry.register(idRef.current, () => check());
    // Intentionally register once per dirty transition; the wrapper closes over the
    // latest `check` via a ref-like closure. Guards are keyed by idRef so re-registration
    // during a dirty session simply replaces the entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registry, dirty]);

  // Sentinel history entry + popstate guard
  useEffect(() => {
    if (!dirty) return;
    const marker = { __unsavedSentinel: true, panelId: idRef.current, ts: Date.now() };
    // Only push a sentinel if we don't already have one on top (avoids stacking under StrictMode)
    const already = window.history.state && window.history.state.__unsavedSentinel;
    if (!already) window.history.pushState(marker, "", window.location.href);

    const onPop = async () => {
      if (!dirtyRef.current) return;
      // Re-push the sentinel so we stay in place while prompting
      window.history.pushState(marker, "", window.location.href);
      const ok = await check();
      if (ok) {
        // Pop the sentinel, then go back to the real previous entry
        window.history.go(-2);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      // NOTE: We intentionally do NOT `history.back()` on cleanup. The sentinel is
      // just an extra history entry with the same URL — leaving it in place is
      // harmless and avoids triggering a rogue navigation when a panel unmounts
      // (e.g. Radix Tabs hides the inactive tab content).
    };
  }, [dirty, check]);

  // Beforeunload (real refresh / tab close)
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  return null;
}

// --- Global click interceptor (mounted once near the app root) --------------
/**
 * Installs a document-level click listener on the CAPTURE phase so it runs
 * before react-router's own Link onClick. If any dirty guard is active and
 * the clicked target is an in-app <a>, we call e.preventDefault() SYNCHRONOUSLY,
 * then run the async confirm, and only re-issue the navigation on approval.
 */
export function UnsavedChangesLinkInterceptor() {
  const registry = useUnsavedRegistry();
  useEffect(() => {
    if (!registry) return;
    const handler = async (e) => {
      if (!registry.anyDirty()) return;
      const a = e.target.closest("a");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (!href.startsWith("/") || a.target === "_blank" || a.hasAttribute("download")) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      // Same URL? Let it through
      if (href === window.location.pathname + window.location.search) return;

      e.preventDefault();
      e.stopPropagation();
      const ok = await registry.runAll();
      if (ok) {
        // The user has confirmed; the guard(s) auto-unregistered on save.
        // Simulate a fresh click programmatically now that no guard blocks us.
        // Use react-router's navigate via a real link click (dispatch a synthetic click without our listener).
        a.removeEventListener?.("click", handler, true);
        // Directly navigate — the guards are cleared, so a plain history push works.
        window.history.pushState({}, "", href);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [registry]);
  return null;
}

// --- Guarded Radix Tabs onValueChange helper -------------------------------
export function useGuardedTabsChange(setter) {
  const registry = useUnsavedRegistry();
  return useCallback(async (next) => {
    if (registry?.anyDirty()) {
      const ok = await registry.runAll();
      if (!ok) return;
    }
    setter(next);
  }, [registry, setter]);
}
